import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useState,
  Fragment,
  useCallback, useEffect,
} from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  sortingFns,
  Table,
  Header,
  ColumnFiltersState,
  GroupingState,
} from "@tanstack/react-table";
import type {
  ColumnDef,
  ExpandedState,
  ColumnPinningState,
  SortingState,
  PaginationState,
  OnChangeFn,
  RowData,
  RowSelectionState,
  Row,
} from "@tanstack/react-table";

import classNames from "classnames";

import { useVirtual } from "react-virtual";
import { HTMLTableProps, Classes, Icon } from "@blueprintjs/core";
import { MultiModal, MultiModalRef } from "@/components";

import { exportExcel } from "@/shared/libs/exceljs";
import { last } from "lodash-es";
import { makeColumn } from "./column";
import { Filter } from "./Filter";

import {
  TableStyled,
  HeaderContent,
  TableResizer,
  TableContainer,
} from "./Styles";
import {
  TableHeader,
  TableBody,
  TableFooter,
  TableTH,
  TableTD,
} from "./Styles";

import type { Column, ActionsType } from "./type";
import Pagination from "./pagination";

export type TableRef = { api: Table<any>; export: () => Promise<string> };
export type { ColumnDef, Column, ActionsType, PaginationState };
export interface BasicTableProps extends HTMLTableProps {
  data: any[];
  onDataChange?: (data: any[]) => void;
  columns: Column<any>[];
  columnPinning?: ColumnPinningState;
  actions?: ActionsType;
  manualColumns?: ColumnDef<Record<string, any>>[];
  visibleFooter?: boolean;
  scroll?: { width: number; height: number };
  selected?: boolean;
  childrenKey?: string;
  skeleton?: boolean;
  rowKey?: string;
  onRowClick?: (row: Record<string, any>) => void;
  total?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  getRowCanExpand?: (row: Row<any>) => boolean;
  onRowSelectorChange?: OnChangeFn<RowSelectionState>;
  cancelSelected?:{cancelFlag:boolean,timestamp:string}; //取消所有选中
  renderSubComponent?: (data: { row: Row<any> }) => JSX.Element;
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: any) => void;
    updateMulti: (rowIndex: number, values: Record<string, any>) => void;
    removeData: (rowIndex: number) => void;
    insertData: (value: any | any[]) => void;
  }
}

function BasicTable(
  {
    data,
    onDataChange,
    columns,
    actions,
    manualColumns,
    scroll,
    childrenKey = "children",
    visibleFooter,
    total,
    pagination,
    onPaginationChange,
    selected,
    onRowClick,
    skeleton,
    rowKey = "id",
    getRowCanExpand,
    onRowSelectorChange,
    cancelSelected,
    renderSubComponent,
    columnPinning: cP,
    ...htmlTableProps
  }: BasicTableProps,
  ref: any
) {
  const modalRef = useRef<MultiModalRef>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [filtersState, setFiltersState] = useState<ColumnFiltersState>([]);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(() => {
    if (cP) return cP;
    const leftList = columns
      .filter((item) => item.pin === "left")
      .map((item) => item.field) as string[];

    if (selected && leftList.length) {
      leftList.unshift("select");
    }

    const rightList = columns
      .filter((item) => item.pin === "right")
      .map((item) => item.field) as string[];

    if (typeof actions === "object") {
      if (actions.pin === "left"){
        leftList.push("actions");
      }
      if (actions.pin === "right"){
        rightList.push("actions");
      }
      if (!actions.pin){
        rightList.push("actions");
      }
    }
    return {
      left: leftList,
      right: rightList.concat("options"),
    };
  });

  const tableColumn = useMemo(
    () => makeColumn({ columns, selected, actions }),
    [columns, selected, actions]
  );
  const pageCount = useMemo(() => {
    if (!pagination || !total) return undefined;
    const { pageSize } = pagination;
    return Math.ceil(total / pageSize);
  }, [total, pagination]);

  const handleRowSelector = useCallback<OnChangeFn<RowSelectionState>>(
    (state) => {
      onRowSelectorChange?.(state);
      setRowSelection(state);
    },
    [onRowSelectorChange]
  );

  //取消选中的，使用useEffect进行监听
  useEffect(()=>{
    if(cancelSelected?.cancelFlag){
      setRowSelection({});
    }
  },[cancelSelected]);

  const reactTable = useReactTable({
    data,
    state: {
      expanded,
      columnPinning,
      pagination,
      rowSelection,
      sorting,
      columnFilters: filtersState,
      grouping,
    },
    columns: manualColumns ?? tableColumn,
    enableRowSelection: selected,
    columnResizeMode: "onChange",
    onExpandedChange: setExpanded,
    getSubRows: (row) => row[childrenKey] ?? [],
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand,
    getGroupedRowModel: getGroupedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnPinningChange: setColumnPinning,
    onPaginationChange: onPaginationChange,
    onRowSelectionChange: handleRowSelector,
    onColumnFiltersChange: setFiltersState,
    onGroupingChange: setGrouping,
    onSortingChange: setSorting,
    getRowId: (originalRow, index) => {
      if (rowKey) return originalRow[rowKey] as string;
      return index + "";
    },
    manualPagination: true,
    pageCount,
    sortingFns,
    meta: {
      updateData: (rowIndex, id, value) => {
        const newData = data.map((item, index) => {
          if (index === rowIndex) {
            return {
              ...item,
              [id]: value,
            };
          }
          return item;
        });

        onDataChange?.(newData);
      },
      updateMulti: (rowIndex, values) => {
        const newData = data.map((item, index) => {
          if (index === rowIndex) {
            return {
              ...item,
              ...values,
            };
          }
          return item;
        });

        onDataChange?.(newData);
      },
      removeData(rowIndex) {
        const newData = data.filter((_v, index) => index !== rowIndex);
        onDataChange?.(newData);
      },
      insertData(value) {
        onDataChange?.(data.concat(value));
      },
    },
  });

  useImperativeHandle(ref, () => ({
    api: reactTable,
    export: () => exportExcel(reactTable),
  }));

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { getHeaderGroups, getRowModel, getFooterGroups } = reactTable;
  const { rows } = getRowModel();

  // 虚拟列表相关的变量
  const { virtualItems, totalSize } = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 40,
  });
  const paddingTop = virtualItems[0]?.start ?? 0;
  const paddingBottom = virtualItems.length
    ? totalSize - (last(virtualItems)?.end ?? 0)
    : 0;

  return (
    <Fragment>
      <TableContainer
        scroll={scroll}
        showPagination={!!pagination}
        className={classNames({ [Classes.SKELETON]: skeleton })}
        ref={tableContainerRef}
      >
        <TableStyled condensed striped interactive {...htmlTableProps}>
          <TableHeader>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const headerWidth = header.getSize();

                  const isPin = header.column.getIsPinned();
                  let stickWidth = 0;
                  let isLeftPinLast = false;
                  let isRightPinFirst = false;
                  if (isPin === "left") {
                    const leftHeaders = headerGroup.headers.slice(0, index);
                    stickWidth = leftHeaders.reduce((prev, header) => {
                      prev += header.getSize();
                      return prev;
                    }, 0);

                    const nextHeader = headerGroup.headers[index + 1];
                    if (!nextHeader?.column.getIsPinned()) isLeftPinLast = true;
                  }

                  if (isPin === "right") {
                    const leftHeaders = headerGroup.headers.slice(index + 1);
                    stickWidth = leftHeaders.reduce((prev, header) => {
                      prev += header.getSize();
                      return prev;
                    }, 0);

                    const prevHeader = headerGroup.headers[index - 1];
                    if (!prevHeader?.column.getIsPinned())
                      isRightPinFirst = true;
                  }

                  const isSorted = header.column.getIsSorted();
                  return (
                    <TableTH
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        // 用于空白占位符，如果设定的 size 大于 99999，则此行的长度为 auto
                        width: headerWidth > 99999 ? "auto" : headerWidth,
                      }}
                      pin={isPin}
                      stickyWidth={stickWidth}
                      isLeftPinLast={isLeftPinLast}
                      isRightPinFirst={isRightPinFirst}
                      onDoubleClick={() => {
                        if (header.column.getCanGroup()) {
                          header.column.toggleGrouping();
                        }
                      }}
                    >
                      <HeaderContent>
                        {header.column.getCanGroup() &&
                          header.column.getIsGrouped() && (
                            <Icon
                              icon="filter"
                              size={12}
                              className="cursor-pointer mr-1"
                              intent="primary"
                              onClick={header.column.getToggleGroupingHandler()}
                              title="分组汇总"
                            />
                          )}
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getCanFilter() && (
                          <Icon
                            icon="inbox-filtered"
                            className="cursor-pointer ml-1"
                            size={14}
                            intent={
                              header.column.getFilterValue()
                                ? "primary"
                                : "none"
                            }
                            color={
                              header.column.getFilterValue()
                                ? undefined
                                : "lightgray"
                            }
                            onClick={() => {
                              modalRef.current?.open({
                                header,
                                table: reactTable,
                              });
                            }}
                            title="筛选"
                          />
                        )}
                        <div style={{ flexGrow: 1 }} />
                        {header.column.getCanSort() && (
                          <div
                            onClick={header.column.getToggleSortingHandler()}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            {isSorted ? (
                              isSorted === "desc" ? (
                                <Icon icon="caret-down" intent="primary" />
                              ) : (
                                <Icon icon="caret-up" intent="primary" />
                              )
                            ) : (
                              <Icon
                                icon="double-caret-vertical"
                                color="lightgray"
                                title="排序"
                              />
                            )}
                          </div>
                        )}
                      </HeaderContent>
                      {header.column.getCanResize() && (
                        <TableResizer
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          resizing={header.column.getIsResizing()}
                        />
                      )}
                    </TableTH>
                  );
                })}
              </tr>
            ))}
          </TableHeader>
          <TableBody>
            {!!paddingTop && <VirtualPadding height={paddingTop} />}
            {virtualItems.map(({ index }) => {
              const row = rows[index];
              const visibleCell = row.getVisibleCells();

              return (
                <Fragment key={row.id}>
                  <tr onClick={onRowClick?.bind(undefined, row.original)}>
                    {visibleCell.map((cell, index) => {
                      const isPin = cell.column.getIsPinned();
                      let stickWidth = 0;
                      let isLeftPinLast = false;
                      let isRightPinFirst = false;

                      if (isPin === "left") {
                        const leftHeaders = visibleCell.slice(0, index);
                        stickWidth = leftHeaders.reduce((prev, cell) => {
                          prev += cell.column.getSize();
                          return prev;
                        }, 0);

                        const nextCell = visibleCell[index + 1];
                        if (!nextCell?.column.getIsPinned())
                          isLeftPinLast = true;
                      }

                      if (isPin === "right") {
                        const leftHeaders = visibleCell.slice(index + 1);
                        stickWidth = leftHeaders.reduce((prev, cell) => {
                          prev += cell.column.getSize();
                          return prev;
                        }, 0);

                        const prevCell = visibleCell[index + 1];
                        if (!prevCell?.column.getIsPinned())
                          isRightPinFirst = true;
                      }
                      return (
                        <TableTD
                          key={cell.id}
                          style={{ width: cell.column.getSize() }}
                          pin={isPin}
                          stickyWidth={stickWidth}
                          isLeftPinLast={isLeftPinLast}
                          isRightPinFirst={isRightPinFirst}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableTD>
                      );
                    })}
                  </tr>
                  {row.getIsExpanded() && !!renderSubComponent && (
                    <tr>
                      <td colSpan={row.getVisibleCells().length}>
                        {renderSubComponent({ row: row })}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!!paddingBottom && <VirtualPadding height={paddingBottom} />}
          </TableBody>
          {visibleFooter && !grouping.length && (
            <TableFooter>
              {getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((footer, index) => {
                    const isPin = footer.column.getIsPinned();
                    let stickWidth = 0;
                    let isLeftPinLast = false;
                    let isRightPinFirst = false;
                    if (isPin === "left") {
                      const leftHeaders = footerGroup.headers.slice(0, index);
                      stickWidth = leftHeaders.reduce((prev, header) => {
                        prev += header.getSize();
                        return prev;
                      }, 0);

                      const nextHeader = footerGroup.headers[index + 1];
                      if (!nextHeader?.column.getIsPinned())
                        isLeftPinLast = true;
                    }

                    if (isPin === "right") {
                      const leftHeaders = footerGroup.headers.slice(index + 1);
                      stickWidth = leftHeaders.reduce((prev, header) => {
                        prev += header.getSize();
                        return prev;
                      }, 0);

                      const prevHeader = footerGroup.headers[index - 1];
                      if (!prevHeader?.column.getIsPinned())
                        isRightPinFirst = true;
                    }

                    return (
                      <TableTH
                        key={footer.id}
                        colSpan={footer.colSpan}
                        pin={isPin}
                        stickyWidth={stickWidth}
                        isLeftPinLast={isLeftPinLast}
                        isRightPinFirst={isRightPinFirst}
                      >
                        {flexRender(
                          footer.column.columnDef.footer,
                          footer.getContext()
                        )}
                      </TableTH>
                    );
                  })}
                </tr>
              ))}
            </TableFooter>
          )}
        </TableStyled>
      </TableContainer>
      {pagination && <Pagination table={reactTable} totalSize={total} />}
      <MultiModal
        ref={modalRef}
        modalWidth={320}
        modalHeight={136}
        titleRender={({ header }: { header: Header<any, unknown> }) =>
          header.column.columnDef.header?.toString() + "筛选"
        }
        contentRender={({ id, item }) => {
          return (
            <Filter
              key={id}
              header={item.header}
              table={item.table}
              onClose={() => modalRef.current?.close(id)}
            />
          );
        }}
      />
    </Fragment>
  );
}

export default forwardRef<TableRef, BasicTableProps>(BasicTable);

interface IVirtualPaddingProps {
  height: number;
}
const VirtualPadding = ({ height }: IVirtualPaddingProps) => (
  <tr>
    <td style={{ height }} />
  </tr>
);
